import { Button } from '../ui/button'
import { Input } from '../ui/input/input'
import React, { useState } from 'react'
import { GroupType, GroupVisibility, type CreateGroupPayload } from '../../types/groups'
import { useCreateAdminGroup } from '../../hook/use-groups'

interface CreateGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export const CreateGroupModal = ({ isOpen, onClose, onSuccess }: CreateGroupModalProps): React.ReactElement | null => {
  const [formData, setFormData] = useState<CreateGroupPayload>({
    name: '',
    description: '',
    type: GroupType.GENERAL,
    visibility: GroupVisibility.PRIVATE,
    hustlerId: '',
  })

  const createMutation = useCreateAdminGroup()

  const handleSubmit = (): void => {
    createMutation.mutate(formData, {
      onSuccess: () => {
        onSuccess()
        onClose()
        setFormData({
          name: '',
          description: '',
          type: GroupType.GENERAL,
          visibility: GroupVisibility.PRIVATE,
          hustlerId: '',
        })
      },
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-dark-gray rounded-lg p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-ivory font-evogria text-2xl mb-6">Create New Group</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Group Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter group name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              rows={3}
              value={formData.description ?? ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter description"
              className="w-full bg-secondary-gray text-light-gray rounded-md px-4 py-2 text-sm placeholder:text-mushroom focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Owner Hustler ID</label>
            <Input
              value={formData.hustlerId}
              onChange={(e) => setFormData({ ...formData, hustlerId: e.target.value })}
              placeholder="Enter hustler ID"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as GroupType })}
              className="w-full bg-gray-800 text-ivory border border-gray-700 rounded px-3 py-2"
            >
              <option value={GroupType.GENERAL}>General</option>
              <option value={GroupType.CHAMPIONS}>Champions</option>
              <option value={GroupType.COMPANY}>Company</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Visibility</label>
            <select
              value={formData.visibility}
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value as GroupVisibility })}
              className="w-full bg-gray-800 text-ivory border border-gray-700 rounded px-3 py-2"
            >
              <option value={GroupVisibility.PRIVATE}>Private</option>
              <option value={GroupVisibility.PUBLIC}>Public</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleSubmit} disabled={!formData.name || !formData.hustlerId || createMutation.isLoading}>
            {createMutation.isLoading ? 'Creating...' : 'Create Group'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
